import React from 'react';
import { View, StyleProp, ViewStyle } from 'react-native';
import Svg, { Path } from 'react-native-svg';
import { Text } from '@/components/Text';
import { styles } from './styles';
import { Icon } from '@/components/Icon';
import { Button } from '@/components/ui/therapist/Button';
import { IconName } from '@/components/NeolexonIcon/Icons/types';
import { isNeolexonIcon } from '@/components/NeolexonIcon/Icons';
import { NeolexonIcon } from '@/components/NeolexonIcon';
import { IS_WEB } from '@/utils/platform';
import { isPatientApp } from '@/config/constants';
import colors from '@/config/colors';

interface CardProps {
  title?: string;
  icon?: React.ComponentProps<typeof Icon>['name'] | IconName;
  iconCentered?: boolean;
  iconCenter?: React.ComponentProps<typeof Icon>['name'] | IconName;
  iconCenterSize?: number;
  bottomDent?: boolean;
  iconPosition?: 'inside' | 'outside';
  linkText?: string;
  onLinkPress?: () => void;
  children?: React.ReactNode;
  style?: StyleProp<ViewStyle>;
  styleBody?: StyleProp<ViewStyle>;
  testID?: string;
}

export const Card: React.FC<CardProps> = ({
  title,
  icon,
  iconCentered = false,
  iconCenter,
  iconCenterSize = 32,
  bottomDent = false,
  iconPosition = 'inside',
  linkText,
  onLinkPress,
  children,
  style,
  styleBody,
  testID,
}) => {
  const isIconInside = icon && iconPosition === 'inside';
  const isIconOutside = icon && iconPosition === 'outside';
  const showLink = !!(linkText && onLinkPress);
  const hasHeader = !!(title || icon || linkText);

  const renderIcon = (
    icon?: React.ComponentProps<typeof Icon>['name'] | IconName,
  ) =>
    icon ? (
      isNeolexonIcon(icon) ? (
        <NeolexonIcon name={icon} size={iconCenterSize} />
      ) : (
        <Icon
          name={icon}
          style={[styles.icon, isPatientApp && { color: colors.primary }]}
        />
      )
    ) : null;

  const renderLink = () =>
    showLink ? (
      <Button onPress={onLinkPress!} label={linkText!} look="tertiary" />
    ) : null;

  const renderHeaderWithOutsideIcon = () => (
    <View style={styles.headerRow}>
      <View style={styles.iconBox}>{renderIcon(icon)}</View>
      <View style={styles.titleAndLinkContainer}>
        {!!title && <Text style={styles.title}>{title}</Text>}
        {renderLink()}
      </View>
    </View>
  );

  const renderHeaderWithInsideIcon = () => (
    <View style={styles.insideHeaderContainer}>
      <View style={styles.headerRow}>
        <View style={styles.insideHeaderTitleGroup}>
          {isIconInside ? (
            <View style={styles.inlineIcon}>{renderIcon(icon)}</View>
          ) : null}
          {!!title && (
            <Text
              style={[
                styles.title,
                IS_WEB
                  ? ({ wordBreak: 'break-word', flexShrink: 1 } as any)
                  : null,
              ]}
            >
              {title}
            </Text>
          )}
        </View>
        {renderLink()}
      </View>
    </View>
  );

  const renderIconCentered = () => (
    <View style={{ width: '100%', alignItems: 'center', position: 'relative' }}>
      {IS_WEB ? (
        <svg
          width="100%"
          height={40}
          viewBox="0 0 400 40"
          preserveAspectRatio="none"
        >
          <path
            d="M150,40 C170,40 180,10 200,10 C220,10 230,40 250,40 L250,40 L150,40 Z"
            fill="white"
          />
        </svg>
      ) : (
        <Svg
          width="100%"
          height={40}
          viewBox="0 0 400 40"
          preserveAspectRatio="none"
        >
          <Path
            d="M150,40 C170,40 180,10 200,10 C220,10 230,40 250,40 L250,40 L150,40 Z"
            fill="white"
          />
        </Svg>
      )}
      <View style={{ position: 'absolute', bottom: -5 }}>
        {renderIcon(iconCenter)}
      </View>
    </View>
  );

  const renderBottomDent = () => (
    <View
      style={{
        width: '100%',
        borderBottomLeftRadius: 7,
        borderBottomRightRadius: 7,
        overflow: 'hidden',
      }}
    >
      {IS_WEB ? (
        <svg
          width="100%"
          height={40}
          viewBox="0 0 400 40"
          preserveAspectRatio="none"
        >
          <path
            d="M0,0 L0,40 L150,40 C170,40 180,10 200,10 C220,10 230,40 250,40 L400,40 L400,0 Z"
            fill="white"
          />
        </svg>
      ) : (
        <Svg
          width="100%"
          height={40}
          viewBox="0 0 400 40"
          preserveAspectRatio="none"
        >
          <Path
            d="M0,0 L0,40 L150,40 C170,40 180,10 200,10 C220,10 230,40 250,40 L400,40 L400,0 Z"
            fill="white"
          />
        </Svg>
      )}
    </View>
  );

  return (
    <View style={[styles.cardContainer, style]} testID={testID}>
      {iconCentered ? renderIconCentered() : null}
      {hasHeader
        ? isIconOutside
          ? renderHeaderWithOutsideIcon()
          : renderHeaderWithInsideIcon()
        : null}
      <View
        style={[
          styles.cardBody,
          isIconOutside && styles.cardBodyRoundedTopLeft,
          styleBody,
        ]}
      >
        {children}
      </View>
      {bottomDent ? renderBottomDent() : null}
    </View>
  );
};
